import React from 'react';
import LegalLayout from '../components/LegalLayout';

export default function PrivacyPolicyPage() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="April 1, 2026">
      <p>
        Longfloat (“Platform”, “Application”, “Service”, “we”, “our”, or “us”) is owned and operated by Longfloat Information Technology Pvt Ltd.
      </p>
      <p>
        This Privacy Policy explains how we collect, use, store, disclose, and protect your information when you access or use Longfloat, including our website, applications, APIs, integrations, and related services.
      </p>
      <p>By using Longfloat, you agree to the practices described in this Privacy Policy.</p>

      <h2 id="information-we-collect">1. Information We Collect</h2>
      <p>We may collect both Personal Information and Non-Personal Information when you use Longfloat.</p>

      <h3>Personal Information</h3>
      <p>Personal Information may include:</p>
      <ul>
        <li>Name</li>
        <li>Email address</li>
        <li>Phone number</li>
        <li>Company name</li>
        <li>Billing details</li>
        <li>Account credentials</li>
        <li>Communication preferences</li>
        <li>Information submitted through forms or support requests</li>
      </ul>

      <h3>Non-Personal Information</h3>
      <p>Non-Personal Information may include:</p>
      <ul>
        <li>Browser type</li>
        <li>Device information</li>
        <li>IP address</li>
        <li>Operating system</li>
        <li>Usage analytics</li>
        <li>Session information</li>
        <li>Feature interactions</li>
        <li>Cookies and tracking data</li>
      </ul>

      <h2 id="how-we-collect">2. How We Collect Information</h2>
      <p>We collect information through:</p>
      <ul>
        <li>Account registration</li>
        <li>Website forms</li>
        <li>Subscription and billing processes</li>
        <li>Customer support interactions</li>
        <li>Platform usage analytics</li>
        <li>Cookies and similar technologies</li>
        <li>Third-party integrations</li>
        <li>Marketing and communication tools</li>
      </ul>

      <h2 id="how-we-use">3. How We Use Your Information</h2>
      <p>We use collected information to:</p>
      <ul>
        <li>Provide and maintain Longfloat services;</li>
        <li>Improve platform functionality and user experience;</li>
        <li>Process subscriptions and payments;</li>
        <li>Respond to inquiries and support requests;</li>
        <li>Send transactional notifications and updates;</li>
        <li>Detect fraud, abuse, and security threats;</li>
        <li>Analyze platform usage and performance;</li>
        <li>Comply with legal obligations;</li>
        <li>Develop new features and services.</li>
      </ul>
      <p>
        We may also use your information for marketing communications where permitted by law. You may opt out at any time.
      </p>

      <h2 id="ai-features">4. AI Features and Generated Content</h2>
      <p>Longfloat includes artificial intelligence and workflow automation features.</p>
      <p>
        Information you provide through prompts, workflows, uploads, or automation tasks may be processed by AI systems to generate outputs and improve service functionality.
      </p>
      <p>You remain responsible for reviewing and validating AI-generated outputs before use.</p>

      <h2 id="third-party-integrations">5. Third-Party Integrations</h2>
      <p>Longfloat may integrate with third-party services including:</p>
      <ul>
        <li>Slack</li>
        <li>Telegram</li>
        <li>WhatsApp</li>
        <li>Signal</li>
        <li>Google Drive</li>
        <li>Email providers</li>
        <li>Cloud storage providers</li>
        <li>Payment gateways</li>
        <li>External APIs</li>
      </ul>
      <p>
        When you connect third-party services, certain information may be shared as necessary to provide requested functionality.
      </p>
      <p>Your use of third-party services is governed by their own privacy policies and terms.</p>

      <h2 id="cookies">6. Cookies and Tracking Technologies</h2>
      <p>We use cookies and similar technologies for:</p>
      <ul>
        <li>Authentication and session management;</li>
        <li>Security and fraud prevention;</li>
        <li>User preferences and settings;</li>
        <li>Analytics and performance monitoring;</li>
        <li>Marketing and advertising (where permitted).</li>
      </ul>
      <p>
        You may manage cookie preferences through your browser settings. Disabling certain cookies may affect platform functionality.
      </p>

      <h2 id="analytics">7. Analytics and Advertising</h2>
      <p>We may use analytics and advertising services, including tools such as:</p>
      <ul>
        <li>Google Analytics</li>
        <li>Advertising platforms</li>
        <li>Email campaign providers</li>
      </ul>
      <p>
        These services may collect usage information, device identifiers, and engagement metrics to help us improve the Service and communications.
      </p>
      <p>
        You may opt out of certain tracking technologies through your browser or applicable third-party settings.
      </p>

      <h2 id="email">8. Email Communications</h2>
      <p>We may send:</p>
      <ul>
        <li>Account notifications;</li>
        <li>Security alerts;</li>
        <li>Billing updates;</li>
        <li>Product announcements;</li>
        <li>Marketing communications.</li>
      </ul>
      <p>
        You may unsubscribe from marketing emails at any time using the unsubscribe link included in such communications.
      </p>
      <p>Transactional or service-related emails may still be sent when necessary.</p>

      <h2 id="sharing">9. Sharing of Information</h2>
      <p>We do not sell your Personal Information.</p>
      <p>We may share information with:</p>
      <ul>
        <li>Service providers and vendors;</li>
        <li>Payment processors;</li>
        <li>Cloud hosting providers;</li>
        <li>Analytics providers;</li>
        <li>Legal authorities when required by law;</li>
        <li>Affiliates or subsidiaries of Longfloat Information Technology Pvt Ltd.</li>
      </ul>
      <p>
        Information is shared only as necessary to operate and improve the Service or comply with legal obligations.
      </p>

      <h2 id="security">10. Data Security</h2>
      <p>
        We implement reasonable administrative, technical, and organizational safeguards to protect your information from unauthorized access, disclosure, alteration, or destruction.
      </p>
      <p>
        However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.
      </p>

      <h2 id="retention">11. Data Retention</h2>
      <p>We retain your information only for as long as necessary to:</p>
      <ul>
        <li>Provide the Service;</li>
        <li>Comply with legal obligations;</li>
        <li>Resolve disputes;</li>
        <li>Enforce agreements;</li>
        <li>Maintain operational records.</li>
      </ul>
      <p>When data is no longer required, we take reasonable steps to securely delete or anonymize it.</p>

      <h2 id="your-rights">12. Your Rights and Choices</h2>
      <p>Depending on your jurisdiction, you may have rights to:</p>
      <ul>
        <li>Access your personal information;</li>
        <li>Correct inaccurate information;</li>
        <li>Request deletion of your information;</li>
        <li>Restrict or object to processing;</li>
        <li>Withdraw consent;</li>
        <li>Request data portability;</li>
        <li>Opt out of marketing communications.</li>
      </ul>
      <p>
        To exercise these rights, contact us at{' '}
        <a href="mailto:info@longfloat.com">info@longfloat.com</a>.
      </p>

      <h2 id="children">13. Children’s Privacy</h2>
      <p>Longfloat is not intended for children under the age of 16.</p>
      <p>
        We do not knowingly collect Personal Information from children. If we become aware of such collection, we will take appropriate steps to delete the information.
      </p>

      <h2 id="international-transfers">14. International Data Transfers</h2>
      <p>
        Your information may be processed and stored in countries outside your jurisdiction where our infrastructure, service providers, or partners operate.
      </p>
      <p>
        By using Longfloat, you consent to such transfers in accordance with applicable data protection laws.
      </p>

      <h2 id="business-transfers">15. Business Transfers</h2>
      <p>
        In the event of a merger, acquisition, restructuring, financing, or sale of assets, your information may be transferred as part of the business transaction.
      </p>
      <p>
        Any successor entity will continue to protect your information in accordance with this Privacy Policy.
      </p>

      <h2 id="third-party-links">16. Links to Third-Party Websites</h2>
      <p>Longfloat may contain links to third-party websites or services.</p>
      <p>
        We are not responsible for the privacy practices, security, or content of third-party platforms. We encourage you to review their privacy policies before using those services.
      </p>

      <h2 id="changes">17. Changes to This Privacy Policy</h2>
      <p>We may update this Privacy Policy periodically.</p>
      <p>
        Updated versions will be posted with a revised “Last Updated” date. Continued use of the Service after changes become effective constitutes acceptance of the updated Privacy Policy.
      </p>

      <h2 id="contact">18. Contact Us</h2>
      <p>
        If you have questions, concerns, or requests regarding this Privacy Policy or your personal information, contact us at:
      </p>
      <p className="legal-address">
        <span>Longfloat Information Technology Pvt Ltd</span>
        <span>Application: Longfloat</span>
        <span>
          Email:{' '}
          <a href="mailto:info@longfloat.com">info@longfloat.com</a>
        </span>
      </p>
    </LegalLayout>
  );
}
